<template>
  <v-container fluid class="pa-0">
    <ERow>
      <ECol cols="12" class="py-0">
        <ReportTable
          name="pending-share-requests"
          :headers="pendingUsersHeaders"
          item-key="shareeEmail"
          :provider="getPendingShareRequests"
          :filter-fields="filterFields"
          show-expand
          single-expand
          :server-side-pagination="false"
          actions-portal-target="breadcrumbs-right"
          :show-select="canResendShares || canDeleteShares"
        >
          <template #actions-right>
            <v-btn
              v-if="canResendShares"
              small
              color="primary"
              :disabled="btnDisabled"
              @click="resendSelectedShareesShareRequests"
            >
              Resend
              <v-icon right> far fa-paper-plane </v-icon>
            </v-btn>
            <v-btn
              v-if="canDeleteShares"
              small
              color="error"
              :disabled="btnDisabled"
              @click="deleteSelectedSharees"
            >
              Delete
            </v-btn>
          </template>
          <template #expanded-item="{ item }">
            <td :colspan="12" class="pb-3">
              <ShareeRequestsTable
                :share-requests="item.shareRequests"
                :can-delete="canDeleteShares"
                :can-resend="canResendShares"
                @delete-share-requests="deleteAll"
                @resend-share-requests="resendShareRequests"
              />
            </td>
          </template>
          <template #item.shareRequests="{ item }">
            {{ item.shareRequests?.length }}
          </template>
        </ReportTable>
      </ECol>
    </ERow>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue"

import { pendingUsersHeaders } from "@/components/shareRequests/pendingShareRequestsHeaders"
import filterFields from "@/components/shareRequests/pendingShareRequestsSearchFilters"
import ReportTable from "@/components/ReportTable"
import ShareeRequestsTable from "@/components/shareRequests/ShareeRequestsTable"

import { ShareRequestStatuses } from "@/components/constants"
import { ShareRequestStatus } from "@evercam/shared/types/shares"
import { PERMISSIONS } from "@/constants/permissions"

import { AdminApi } from "@evercam/shared/api/adminApi"

import { mapStores } from "pinia"
import { useReportStore } from "@/stores/report"
import { useBreadcrumbStore } from "@/stores/breadcrumb"
import { EvercamApi } from "@evercam/shared/api"

export default Vue.extend({
  meta: {
    requiredPermissions: [PERMISSIONS.USERS.VIEW_SHARES],
  },
  components: {
    ReportTable,
    ShareeRequestsTable,
  },
  data: () => {
    return {
      pendingUsersHeaders,
      filterFields,
      statusList: [
        { value: ShareRequestStatus.Pending, text: "Pending" },
        { value: ShareRequestStatus.Failed, text: "Failed" },
      ],
    }
  },
  head() {
    return {
      title: "Admin - Pending Users",
      meta: [
        { charset: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          hid: "description",
          name: "description",
          content: "",
        },
      ],
    }
  },
  computed: {
    ...mapStores(useReportStore, useBreadcrumbStore),
    btnDisabled() {
      return !this.reportStore.selectedItems?.length
    },
    canResendShares() {
      return this.$permissions.user.can(PERMISSIONS.USERS.RESEND_SHARES)
    },
    canDeleteShares() {
      return this.$permissions.user.can(PERMISSIONS.USERS.DELETE_SHARES)
    },
  },
  created() {
    this.reportStore.searchFilters = {}
    this.initBreadcrumbs()
  },
  methods: {
    getStatus(status) {
      return ShareRequestStatuses.find((el) => el.value === status)?.name
    },
    async getPendingShareRequests(requestParams = { params: {} }) {
      try {
        const { items } = await AdminApi.shareRequests.getShareRequests({
          ...requestParams,
          params: {
            ...requestParams.params,
            limit: 10000,
            status: ShareRequestStatus.Pending,
          },
        })

        const grouped = items.reduce((acc, item) => {
          const email = item.shareeEmail

          if (!acc[email]) {
            acc[email] = {
              shareeEmail: email,
              shareRequests: [],
            }
          }

          acc[email].shareRequests.push({
            ...item,
          })

          return acc
        }, {})

        const computedItems = Object.values(grouped)

        return { items: computedItems }
      } catch (error) {
        console.error("Failed to fetch pending share requests!", error)
        throw error
      }
    },
    initBreadcrumbs() {
      this.breadcrumbStore.breadcrumbs = [
        {
          name: "Home",
          href: "/",
          icon: "fa-house",
        },
        {
          name: "Pending Users",
          icon: "fa-users",
        },
      ]
    },
    async deleteAll(selectedItems) {
      const ids = selectedItems.map(({ id }) => id).join()
      const confirmed = await this.$confirmDialog.open({
        title: "Delete Share Request(s)",
        message:
          "Are you sure you want to delete the seleted camera share request(s)?",
      })

      if (!confirmed) {
        return
      }

      try {
        await AdminApi.shareRequests.deleteShareRequest({
          ids,
        })

        const shareeEmail = selectedItems?.[0]?.shareeEmail
        const shareeIndex = this.reportStore.items.findIndex(
          (s) => s.shareeEmail === shareeEmail
        )

        if (shareeIndex === -1) {
          return
        }

        const sharee = this.reportStore.items[shareeIndex]
        const remainingRequests = sharee.shareRequests.filter(
          (sh) => !ids.includes(sh.id)
        )

        if (!remainingRequests.length) {
          this.reportStore.items.splice(shareeIndex, 1)
        } else {
          this.reportStore.items[shareeIndex].shareRequests = remainingRequests
        }

        this.$notifications.success("Camera Share Request(s) has been deleted!")
      } catch (error) {
        this.$notifications.error({
          text: "Delete camera share request(s) failed!",
          error,
        })
      }
    },
    async resendShareRequests(selectedItems) {
      const confirmed = await this.$confirmDialog.open({
        title: "Resend Share Request(s)",
        message:
          "Are you sure you want to resend the selected camera share request(s)?",
      })

      if (!confirmed) {
        return
      }

      for (let index = 0; index < selectedItems.length; index++) {
        try {
          const shareRequest = selectedItems[index]
          await EvercamApi.shares.resendShareRequest(shareRequest.cameraExid, {
            email: shareRequest.shareeEmail,
            apiId: shareRequest.apiId,
            apiKey: shareRequest.apiKey,
          })
        } catch (error) {
          this.$notifications.error({
            text: "Failed to resend camera share request!",
            error,
          })
        }
      }
      this.$notifications.success(
        "A notification email has been sent to the specified emails addresses."
      )
    },
    async deleteSelectedSharees() {
      const selected = this.reportStore.selectedItems

      if (!selected.length) {
        return
      }

      const confirmed = await this.$confirmDialog.open({
        title: "Delete Sharee(s)",
        message:
          "Are you sure you want to delete the selected sharee(s) and all their share requests?",
      })

      if (!confirmed) return

      try {
        const ids = selected
          .flatMap((s) => s.shareRequests.map((r) => r.id))
          .join()

        await AdminApi.shareRequests.deleteShareRequest({ ids })

        const shareeEmailsToDelete = new Set(selected.map((s) => s.shareeEmail))

        this.reportStore.items = this.reportStore.items.filter(
          (s) => !shareeEmailsToDelete.has(s.shareeEmail)
        )

        this.reportStore.selectedItems = []

        this.$notifications.success(
          "Selected sharees and their requests have been deleted."
        )
      } catch (error) {
        this.$notifications.error({
          text: "Failed to delete sharees and their requests.",
          error,
        })
      }
    },
    async resendSelectedShareesShareRequests() {
      const selected = this.reportStore.selectedItems

      if (!selected.length) {
        return
      }

      const confirmed = await this.$confirmDialog.open({
        title: "Resend Share Request(s)",
        message:
          "Are you sure you want to resend all share requests for the selected sharee(s)?",
      })

      if (!confirmed) {
        return
      }

      const allRequests = selected.flatMap((s) => s.shareRequests)

      try {
        await Promise.allSettled(
          allRequests.map((request) =>
            EvercamApi.shares.resendShareRequest(request.cameraExid, {
              email: request.shareeEmail,
              apiId: request.apiId,
              apiKey: request.apiKey,
            })
          )
        )

        this.$notifications.success(
          "Notification email(s) sent to the specified addresses."
        )
      } catch (error) {
        this.$notifications.error({
          text: "Failed to resend one or more share requests.",
          error,
        })
      }
    },
  },
})
</script>
